import { useMemo } from 'react';
import { Badge } from '@/components/ui';
import { EmailLink } from '@/components/ui/TextLink';
import { formatDateTime } from '@/lib/format';
import type { EmailThreadDetail } from '@/lib/types';
import {
  addressLabel,
  formatBytes,
  sandboxEmailDocument,
  skippedReasonLabel,
} from './email-log-format';

/*
 * The messages of one logged email thread, newest first. Each body renders from
 * `bodyHtml` inside `<iframe sandbox="">` with a strict CSP (see
 * `email-log-format.ts`) — never in this document.
 */

type Message = EmailThreadDetail['messages'][number];
type Addr = Message['from'];
type MessageFile = Message['files'][number];

function AddressList({ label, addresses }: { label: string; addresses: readonly Addr[] }) {
  if (addresses.length === 0) return null;
  return (
    <>
      <dt className="text-fg-muted">{label}</dt>
      <dd className="m-0 flex min-w-0 flex-wrap gap-x-2 gap-y-0.5">
        {addresses.map((addr, index) => (
          <span key={`${addr.address}-${index}`} className="min-w-0">
            <EmailLink address={addr.address}>{addressLabel(addr)}</EmailLink>
            {index < addresses.length - 1 ? ',' : ''}
          </span>
        ))}
      </dd>
    </>
  );
}

function MessageBody({ html, title }: { html: string; title: string }) {
  const doc = useMemo(() => sandboxEmailDocument(html), [html]);
  return (
    <iframe
      title={title}
      srcDoc={doc}
      sandbox=""
      referrerPolicy="no-referrer"
      className="h-72 w-full resize-y rounded-md border border-border bg-white"
    />
  );
}

export function EmailThreadMessages({
  messages,
  onOpenFile,
}: {
  messages: readonly Message[];
  /** Present only when a saved file can be opened (the thread has a company). */
  onOpenFile?: ((file: MessageFile) => void) | undefined;
}) {
  const ordered = useMemo(
    () =>
      [...messages].sort((a, b) => (a.sentAt < b.sentAt ? 1 : a.sentAt > b.sentAt ? -1 : b.id - a.id)),
    [messages],
  );

  return (
    <ol className="m-0 grid list-none gap-4 p-0">
      {ordered.map((message) => (
        <li key={message.id} className="grid min-w-0 gap-2 rounded-lg border border-border bg-surface p-3">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <p className="m-0 min-w-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">
              {message.subject || '(no subject)'}
            </p>
            <time dateTime={message.sentAt} className="shrink-0 text-xs tabular text-fg-muted">
              {formatDateTime(message.sentAt)}
            </time>
          </div>
          <dl className="m-0 grid grid-cols-[3rem_minmax(0,1fr)] gap-x-2 gap-y-0.5 text-xs">
            <dt className="text-fg-muted">From</dt>
            <dd className="m-0 min-w-0">
              <EmailLink address={message.from.address}>{addressLabel(message.from)}</EmailLink>
              {message.senderUser && (
                <span className="text-fg-muted"> · {message.senderUser.name}</span>
              )}
            </dd>
            <AddressList label="To" addresses={message.to} />
            <AddressList label="Cc" addresses={message.cc} />
          </dl>

          {message.source === 'forward' && (
            <p className="m-0 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-muted">
              <Badge tone="neutral">Forwarded</Badge>
              {(message.forwardedBy || message.forwardedAt) && (
                <span className="min-w-0 [overflow-wrap:anywhere]">
                  {message.forwardedBy && <>by {message.forwardedBy.name}</>}
                  {message.forwardedBy && message.forwardedAt && ' · '}
                  {message.forwardedAt && (
                    <time dateTime={message.forwardedAt} className="tabular">
                      {formatDateTime(message.forwardedAt)}
                    </time>
                  )}
                </span>
              )}
            </p>
          )}

          {message.bodyHtml ? (
            <MessageBody html={message.bodyHtml} title={`Message from ${addressLabel(message.from)}`} />
          ) : (
            <p className="m-0 text-xs text-fg-subtle">No message body</p>
          )}

          {message.files.length > 0 && (
            <ul className="m-0 grid list-none gap-1 p-0 text-xs">
              {message.files.map((file) => (
                <li key={file.id} className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                  {onOpenFile && file.attachmentId !== null ? (
                    <button
                      type="button"
                      onClick={() => onOpenFile(file)}
                      className="min-w-0 rounded-sm text-left text-fg underline-offset-2 [overflow-wrap:anywhere] hover:underline focus-visible:underline"
                    >
                      {file.fileName}
                    </button>
                  ) : (
                    <span className="min-w-0 text-fg-secondary [overflow-wrap:anywhere]">{file.fileName}</span>
                  )}
                  <span className="tabular text-fg-muted">{formatBytes(file.sizeBytes)}</span>
                  {file.skippedReason && (
                    <span className="text-warning">{skippedReasonLabel(file.skippedReason)}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ol>
  );
}
